import { EventPageType } from '@graphql/generated-base';

export type RsvpPageVisibilityInput = Readonly<{
  /**
   * BE-derived. `false` means the event has no enabled legacy RSVP questions, so the RSVP page
   * is effectively disabled. Null/undefined means "unknown" — e.g. older cached responses or a
   * BE upstream failure — and fails open so RSVP entry points stay visible without positive proof.
   */
  hasEnabledLegacyRsvpQuestions: Maybe<boolean> | undefined;
  /** The RSVP deadline has passed (LEGRSVP-85). Sourced from `useRsvpDeadlineGuard`, which fails open too. */
  isRsvpClosedByDeadline: boolean;
}>;

/**
 * Collapses the two independent reasons the RSVP page can be hidden into the single decision the
 * filter acts on.
 *
 * Callers must memoize on *this boolean*, not on the two inputs. The reasons resolve at different
 * times — `hasEnabledLegacyRsvpQuestions` arrives with the event query, `isRsvpClosedByDeadline`
 * lands later from an `ssr: false` query — so an event that is already hiding RSVP for the first
 * reason would otherwise re-filter when the second flips true, producing a fresh array with
 * identical content. Once either reason is true the outcome is settled and nothing downstream
 * should observe a change. See `useGuestSitePages` and the identity note on `filterRsvpPage`.
 */
export const shouldHideRsvpPage = ({ hasEnabledLegacyRsvpQuestions, isRsvpClosedByDeadline }: RsvpPageVisibilityInput): boolean =>
  hasEnabledLegacyRsvpQuestions === false || isRsvpClosedByDeadline;

/**
 * Drops the RSVP page from a guest-site pages array when `shouldHideRsvpPage` says so — either the
 * event has no enabled legacy RSVP questions, or the RSVP deadline has passed.
 *
 * Post-deadline hiding matches the CTA-button behavior (`shouldDisplayRSVPButton && !isRsvpClosedByDeadline`)
 * so the nav link disappears from every guest surface that renders a filtered pages list — Brannan
 * inline nav / sticky nav, EventMenu hamburger, MobileTopBarNav, FooterNav. Behavior matches disabling
 * the "Show RSVP on your website" admin toggle, without persisting a server-side change (LEGRSVP-85).
 *
 * Note: joy-web only owns the in-SPA entry points. Hard navigation to `/:eventHandle/rsvp` is passed
 * through to the legacy app-browser controller in the monorepo (`rsvp` is in its `subPathsToPassThrough`)
 * and must be handled there (tracked as LEGRSVP-95). The one in-SPA path to that route is FooterNav's
 * client-side `NavLink`, which today renders empty page chrome because no layout registers an `rsvp`
 * entry in `pageWidgets`. Post-deadline the filter now removes the link, so a soft click can't reach it either.
 *
 * Also note: the layouts feed this result into page *resolution*, not just nav — Brannan multi-page
 * matches `currentPage` against it and redirects to the default page when nothing matches
 * (LayoutBrannan.tsx). Today that's an improvement (a mid-session `/rsvp` URL resolves to the default
 * page instead of empty chrome); if a real `rsvp` widget is ever added, dropping the page here would
 * bounce a guest off `/rsvp` mid-session when the deadline query resolves.
 *
 * Returns the input array by reference whenever the filter is a no-op — not just when nothing should
 * be hidden, but also when there is no RSVP page to drop (the common case once one reason has already
 * hidden it, or for events that never had one). `useGuestSitePages` feeds `useAlohaScroll`, whose
 * `pages`-keyed effect would otherwise re-init the single-page scroll controller mid-session when the
 * deadline query resolves.
 */
export const filterRsvpPage = <P extends Readonly<{ type: EventPageType }>>(pages: ReadonlyArray<P>, isRsvpPageHidden: boolean): ReadonlyArray<P> => {
  if (!isRsvpPageHidden) {
    return pages;
  }
  const next = pages.filter(page => page.type !== EventPageType.rsvp);
  return next.length === pages.length ? pages : next;
};
